<template>
  <div class="price-proposals">
    <div class="d-flex justify-content-between mb-3">
      <div style="display: flex; align-items: center">
        <h4 style="font-size: 14px" class="mr-2">Đề xuất sửa giá</h4>
        <button
          type="button"
          style="padding: 2px 10px; height: 26px"
          class="btn btn-outline-primary"
          @click="toggleProposals"
        >
          {{ showProposals ? 'Ẩn đề xuất sửa giá' : 'Hiện đề xuất sửa giá' }}
        </button>
      </div>
      <button
        style="padding: 2px 10px; height: 26px"
        class="btn btn-success"
        @click="openNewProposal"
        :disabled="loading"
        v-if="dataPermission.includes('CNPS_SCON_APPENDIX_CREATE_PRICE_PROPOSAL')"
      >
        Thêm mới
      </button>
    </div>

    <b-collapse v-model="showProposals">
      <div class="card p-0">
        <div class="card-body p-0">
          <div v-if="loading" class="text-center py-4">
            <b-spinner label="Loading..."></b-spinner>
          </div>
          <div v-else-if="!proposals.length" class="text-center py-4">
            Không có đề xuất sửa giá nào
          </div>
          <div v-else>
            <!-- Proposal Header Information -->
            <div
              class="border-bottom border p-3"
              style="border-radius: 10px"
              v-if="currentProposal"
            >
              <div class="row mb-2">
                <div class="col-md-6 d-flex justify-content-between align-items-center">
                  <p style="font-weight: 600; font-family: 'Roboto', sans-serif">
                    Đề xuất sửa giá thiết bị {{ currentProposal.proposalCode }}
                  </p>
                </div>
                <div class="col-md-6 text-right">
                  <div class="btn-group" v-if="currentProposal.status === 'PENDING'">
                    <button
                      style="padding: 2px 10px; height: 26px"
                      class="btn btn-primary mr-1"
                      v-if="
                        currentProposal.status === 'PENDING' &&
                        dataPermission.includes('CNPS_SCON_APPENDIX_APPROVE_PRICE_PROPOSAL')
                      "
                      @click="showApproveModal(currentProposal.id)"
                      :disabled="loading"
                    >
                      Duyệt
                    </button>
                    <button
                      style="padding: 2px 10px; height: 26px"
                      class="btn btn-warning mr-1"
                      @click="showRejectModal(currentProposal.id)"
                      v-if="
                        currentProposal.status === 'PENDING' &&
                        dataPermission.includes('CNPS_SCON_APPENDIX_APPROVE_PRICE_PROPOSAL')
                      "
                    >
                      Từ chối
                    </button>
                    <button
                      style="padding: 2px 10px; height: 26px"
                      class="btn btn-danger"
                      @click="showCancelModal(currentProposal.id)"
                      v-if="
                        currentProposal.status === 'PENDING' &&
                        dataPermission.includes('CNPS_SCON_APPENDIX_CREATE_PRICE_PROPOSAL')
                      "
                    >
                      Huỷ
                    </button>
                  </div>
                </div>
              </div>
              <div class="row">
                <div class="col-md-6">
                  <div class="mb-2">
                    Trạng thái:
                    <b-badge :variant="getStatusVariant(currentProposal.status)">
                      {{ getStatusText(currentProposal.status) }}
                    </b-badge>
                  </div>
                  <div class="mb-2">Người tạo: {{ currentProposal.createdByEmail || '' }}</div>
                  <div class="mb-2">Thời gian tạo: {{ formatDate(currentProposal.createdAt) }}</div>
                  <div class="mb-2" v-if="getActionDate(currentProposal)">
                    Thời gian {{ getActionDateName(currentProposal) }}:
                    {{ getActionDate(currentProposal) }}
                  </div>
                  <div v-if="currentProposal.rejectReason">
                    Lý do từ chối: {{ currentProposal.rejectReason }}
                  </div>
                </div>
              </div>
              <!-- Devices Table -->
              <b-table
                responsive
                :items="currentDevices"
                :fields="deviceFields"
                class="mb-0 mt-1"
                sticky-header
                :fixed="true"
                :bordered="true"
              >
                <template #cell(oldPrice)="data">
                  {{ formatCurrency(data.value) }}
                </template>
                <template #cell(newPrice)="data">
                  {{ formatCurrency(data.value) }}
                </template>
              </b-table>
            </div>
          </div>
        </div>
      </div>
    </b-collapse>

    <!-- Price Proposal Modal -->
    <PriceProposalModal
      ref="priceProposalModal"
      :appendix-id="appendixId"
      :devices-list-add="devicesListAdd"
      @proposal-created="onProposalCreated"
    />

    <!-- Reject Reason Modal -->
    <b-modal
      :id="`reject-modal-${appendixId}`"
      title="Xác nhận từ chối đề xuất"
      ok-title="Xác nhận"
      cancel-title="Huỷ"
      @ok="confirmReject"
    >
      <p>Xác nhận từ chối đề xuất</p>
      <div class="form-group">
        <label for="reject-reason">Lý do từ chối</label>
        <b-form-textarea
          id="reject-reason"
          v-model="rejectReason"
          placeholder="Nhập lý do từ chối"
          rows="3"
        ></b-form-textarea>
      </div>
    </b-modal>

    <!-- Approve Confirmation Modal -->
    <b-modal
      :id="`approve-modal-${appendixId}`"
      title="Xác nhận duyệt đề xuất"
      ok-title="Xác nhận"
      cancel-title="Huỷ"
      @ok="confirmApprove"
    >
      <p>Bạn có chắc chắn muốn duyệt đề xuất?</p>
    </b-modal>

    <!-- Cancel Confirmation Modal -->
    <b-modal
      :id="`cancel-modal-${appendixId}`"
      title="Xác nhận huỷ đề xuất"
      ok-title="Xác nhận"
      cancel-title="Huỷ"
      @ok="confirmCancel"
    >
      <p>Bạn có chắc chắn muốn huỷ đề xuất?</p>
    </b-modal>
  </div>
</template>

<script>
  import { ContractService } from '@/services/contract.service'
  import PriceProposalModal from './PriceProposalModal.vue'
  import { mapGetters } from 'vuex'

  export default {
    name: 'PriceProposals',
    components: {
      PriceProposalModal,
    },
    props: {
      appendixId: {
        type: String,
        required: true,
      },
      devicesListAdd: {
        type: Array,
        required: true,
        default: () => [],
      },
    },
    data() {
      return {
        proposals: [],
        currentProposalIndex: 0,
        totalProposals: 0,
        loading: false,
        showProposals: false,
        currentPage: 1,
        perPage: 10,
        rejectReason: '',
        pendingRejectId: null,
        pendingApproveId: null,
        pendingCancelId: null,
        deviceFields: [
          { key: 'serialNumber', label: 'Thiết bị' },
          { key: 'oldPrice', label: 'Giá cũ', class: 'text-right' },
          { key: 'newPrice', label: 'Giá mới', class: 'text-right' },
          { key: 'reason', label: 'Lý do' },
        ],
      }
    },
    computed: {
      ...mapGetters({
        dataPermission: 'role/dataPermission',
      }),
      currentProposal() {
        if (!this.proposals.length || this.currentProposalIndex >= this.proposals.length) {
          return null
        }
        return this.proposals[this.currentProposalIndex]
      },
      currentDevices() {
        if (!this.currentProposal) {
          return []
        }
        return this.currentProposal.devices || []
      },
    },
    methods: {
      toggleProposals() {
        this.showProposals = !this.showProposals

        if (this.showProposals && this.proposals.length === 0) {
          this.getProposals()
        }
      },

      selectProposal(index) {
        this.currentProposalIndex = index
      },

      formatCurrency(value) {
        return new Intl.NumberFormat('vi-VN', {
          style: 'currency',
          currency: 'VND',
        }).format(value)
      },

      formatDate(timestamp) {
        if (!timestamp) return 'N/A'
        const date = new Date(timestamp)
        return new Intl.DateTimeFormat('vi-VN', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        }).format(date)
      },

      getStatusVariant(status) {
        const variantMap = {
          PENDING: 'primary',
          APPROVED: 'success',
          REJECTED: 'danger',
          CANCELLED: 'danger',
        }
        return variantMap[status] || 'light'
      },

      getStatusText(status) {
        const statusMap = {
          PENDING: 'Mới tạo',
          APPROVED: 'Đã duyệt',
          REJECTED: 'Từ chối',
          CANCELLED: 'Đã hủy',
        }
        return statusMap[status] || status
      },

      getActionDate(proposal) {
        if (proposal.approvedAt) {
          return this.formatDate(proposal.approvedAt)
        } else if (proposal.rejectedAt) {
          return this.formatDate(proposal.rejectedAt)
        }
        return ''
      },

      getActionDateName(proposal) {
        if (proposal.approvedAt) {
          return 'duyệt'
        } else if (proposal.rejectedAt) {
          return 'từ chối'
        }
        return ''
      },

      prepareSearchParams() {
        return {
          appendixId: this.appendixId,
          page: this.currentPage,
          limit: this.perPage,
        }
      },

      async getProposals() {
        try {
          this.loading = true
          const params = this.prepareSearchParams()
          const response = await ContractService.getPriceProposals(params)

          if (response.success) {
            this.proposals = response.data || []
            this.currentProposalIndex = this.proposals.length > 0 ? 0 : -1
            this.totalProposals = response?.totalPage?.total || 0
          } else {
            this.showFailMx(response.message)
          }
        } catch (error) {
          console.error('Error fetching proposals:', error)
          this.showFailMx(error.message)

          this.proposals = []
          this.currentProposalIndex = -1
          this.totalProposals = 0
        } finally {
          this.loading = false
        }
      },

      openNewProposal() {
        // Check if there are devices available for a new proposal
        if (!this.devicesListAdd || this.devicesListAdd.length === 0) {
          this.showFailMx('Không có thiết bị nào để tạo đề xuất sửa giá')
          return
        }

        // Show the modal for creating a new proposal
        this.$refs.priceProposalModal.showModal()
      },

      onProposalCreated() {
        // Refresh proposals list after a new one is created
        this.getProposals()
      },

      // Methods for showing confirmation modals
      showApproveModal(proposalId) {
        this.pendingApproveId = proposalId
        this.$bvModal.show(`approve-modal-${this.appendixId}`)
      },

      showRejectModal(proposalId) {
        this.pendingRejectId = proposalId
        this.rejectReason = ''
        this.$bvModal.show(`reject-modal-${this.appendixId}`)
      },

      showCancelModal(proposalId) {
        this.pendingCancelId = proposalId
        this.$bvModal.show(`cancel-modal-${this.appendixId}`)
      },

      // Methods for handling proposal actions
      async confirmApprove() {
        if (!this.pendingApproveId) return

        try {
          this.loading = true
          const response = await ContractService.approveProposalPrice({
            proposalId: this.pendingApproveId,
          })

          if (response.success) {
            this.showSuccessMx('Duyệt đề xuất thành công')
            await this.getProposals()
          } else {
            this.showFailMx(response.message)
          }
        } catch (error) {
          console.error('Error approving proposal:', error)
          this.showFailMx(error.message)
        } finally {
          this.loading = false
          this.pendingApproveId = null
        }
      },

      async confirmReject() {
        if (!this.pendingRejectId) return

        try {
          this.loading = true
          const response = await ContractService.rejectProposalPrice({
            proposalId: this.pendingRejectId,
            rejectReason: this.rejectReason,
          })

          if (response.success) {
            this.showSuccessMx('Từ chối đề xuất thành công')
            await this.getProposals()
          } else {
            this.showFailMx(response.message)
          }
        } catch (error) {
          console.error('Error rejecting proposal:', error)
          this.showFailMx(error.message)
        } finally {
          this.loading = false
          this.pendingRejectId = null
        }
      },

      async confirmCancel() {
        if (!this.pendingCancelId) return

        try {
          this.loading = true
          const response = await ContractService.cancelProposalPrice({
            proposalId: this.pendingCancelId,
          })

          if (response.success) {
            this.showSuccessMx('Hủy đề xuất thành công')
            await this.getProposals()
          } else {
            this.showFailMx(response.message)
          }
        } catch (error) {
          console.error('Error cancelling proposal:', error)
          this.showFailMx(error.message)
        } finally {
          this.loading = false
          this.pendingCancelId = null
        }
      },

      showSuccessMx(message) {
        this.$bvToast.toast(message || 'Thao tác thành công', {
          title: 'Thông báo',
          variant: 'success',
          solid: true,
        })
      },

      showFailMx(message) {
        this.$bvToast.toast(message || 'Đã xảy ra lỗi', {
          title: 'Thông báo',
          variant: 'danger',
          solid: true,
        })
      },
    },
  }
</script>

<style scoped>
  .price-proposals {
    margin-top: 16px;
  }
  :deep(.b-table-sticky-header) {
    border-radius: 0 !important;
  }
  :deep(.b-table-sticky-header) {
    min-height: fit-content !important;
  }
</style>
